import React from 'react';
import { Link, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import providerService from '@/services/providerService';
import { Provider } from '@/types';
import ProviderDetailContent from './ProviderDetailContent';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const DetailPlaceholder: React.FC = () => (
  <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-3 text-center px-6">
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
      <MaterialIcon icon="local_hospital" style={{ fontSize: 32 }} />
    </div>
    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Sélectionnez un prestataire</h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
      Cliquez sur un prestataire pour voir ses coordonnées et l'itinéraire.
    </p>
  </div>
);

const ProviderListPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const detailMatch = useMatch('/providers/:id');
  const selectedId = detailMatch?.params.id;
  const isDetailView = !!selectedId;
  const [viewMode, setViewMode] = React.useState<'list' | 'map'>('list');

  const name = searchParams.get('name') || '';
  const type = searchParams.get('type') || '';
  const city = searchParams.get('city') || '';

  const { data: providers, isLoading, error } = useQuery({
    queryKey: ['providers', name, type, city],
    queryFn: () => providerService.searchProviders({ name, type, city }),
  });

  const handleBack = () => {
    if (isDetailView) navigate(`/providers/list?${searchParams.toString()}`);
    else navigate('/providers');
  };

  // Map view stays full-width (no master-detail in map mode).
  const showMasterDetailLayout = viewMode === 'list';

  const viewToggle = (
    <div className="flex p-1 bg-gray-100 dark:bg-white/10 rounded-lg">
      <button
        type="button"
        onClick={() => setViewMode('list')}
        aria-label="Vue liste"
        className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-surface-dark shadow-sm text-primary' : 'text-gray-400'}`}
      >
        <MaterialIcon icon="format_list_bulleted" style={{ fontSize: 18 }} />
      </button>
      <button
        type="button"
        onClick={() => setViewMode('map')}
        aria-label="Vue carte"
        className={`p-1.5 rounded-md transition-colors ${viewMode === 'map' ? 'bg-white dark:bg-surface-dark shadow-sm text-primary' : 'text-gray-400'}`}
      >
        <MaterialIcon icon="map" style={{ fontSize: 18 }} />
      </button>
    </div>
  );

  return (
    <AppShell
      title={isDetailView ? 'Fiche prestataire' : 'Résultats'}
      showBack
      backLabel={isDetailView ? 'Résultats' : 'Retour'}
      onBack={handleBack}
      rightElement={!isDetailView ? viewToggle : undefined}
      wide={showMasterDetailLayout}
    >
      {error ? (
        <div className="flex flex-col items-center justify-center py-10 text-center p-4">
          <MaterialIcon icon="error_outline" className="text-4xl text-gray-400 mb-2" />
          <p className="text-gray-500">Une erreur est survenue lors de la recherche.</p>
        </div>
      ) : viewMode === 'map' ? (
        <div className="flex-1 relative bg-gray-200 dark:bg-gray-800 h-full min-h-[500px]">
          {providers && providers.length > 0 ? (
            <MapContainer
              center={[14.7167, -17.4677]}
              zoom={13}
              scrollWheelZoom={false}
              style={{ height: '100%', width: '100%', minHeight: '500px' }}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {providers.map((provider) =>
                provider.latitude && provider.longitude ? (
                  <Marker
                    key={provider.id}
                    position={[provider.latitude, provider.longitude]}
                  >
                    <Popup>
                      <div className="p-1">
                        <h3 className="font-bold text-sm">{provider.name}</h3>
                        <p className="text-xs text-gray-500 capitalize">{provider.type}</p>
                        <p className="text-xs mt-1">{provider.address}</p>
                        <button
                          type="button"
                          onClick={() => navigate(`/providers/${provider.id}`)}
                          className="mt-2 text-xs text-primary font-bold underline"
                        >
                          Voir la fiche
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ) : null
              )}
            </MapContainer>
          ) : (
            <div className="flex h-full items-center justify-center p-4">
              <p className="text-gray-500">Aucune donnée de localisation disponible pour ces résultats.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col md:flex-row md:gap-6 md:pt-4">
          {/* List column */}
          <aside
            className={`flex flex-col gap-4 p-4 md:p-0 md:w-[400px] md:shrink-0 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto md:pr-2 ${
              isDetailView ? 'hidden md:flex' : 'flex'
            }`}
          >
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 w-full bg-gray-100 dark:bg-white/5 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : providers && providers.length > 0 ? (
              <ul className="flex flex-col gap-3">
                {providers.map((provider: Provider) => {
                  const isSelected = provider.id === selectedId;
                  return (
                    <li key={provider.id}>
                      <Link
                        to={`/providers/${provider.id}`}
                        aria-current={isSelected ? 'page' : undefined}
                        className={`flex items-start gap-4 rounded-xl bg-white dark:bg-surface-dark p-4 shadow-sm border transition-all active:scale-[0.99] ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/30'
                            : 'border-gray-100 dark:border-white/5 hover:border-primary/30 hover:shadow-md'
                        }`}
                      >
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <MaterialIcon icon="local_hospital" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 dark:text-white truncate">{provider.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{provider.type}</p>
                          <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                            <MaterialIcon icon="location_on" className="text-[14px]" />
                            <span className="truncate">{provider.city}{provider.address ? `, ${provider.address}` : ''}</span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <MaterialIcon icon="search_off" className="text-4xl text-gray-400 mb-2" />
                <p className="text-gray-500">Aucun prestataire trouvé pour ces critères.</p>
              </div>
            )}
          </aside>

          {/* Detail column */}
          <section
            className={`flex-1 p-4 md:p-0 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto ${
              isDetailView ? 'flex flex-col' : 'hidden md:flex md:flex-col'
            }`}
          >
            {selectedId ? <ProviderDetailContent id={selectedId} /> : <DetailPlaceholder />}
          </section>
        </div>
      )}
    </AppShell>
  );
};

export default ProviderListPage;
